import { useState, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import HeroSection from "./components/HeroSection";
import FeaturesGrid from "./components/FeaturesGrid";
import HowItWorks from "./components/HowItWorks";
import ExampleVideos from "./components/ExampleVideos";
import SocialProof from "./components/SocialProof";
import FeedbackSection from "./components/FeedbackSection";
import HistorySection from "./components/HistorySection";
import ComparePanel from "./components/ComparePanel";
import CommentsAnalysis from "./components/CommentsAnalysis";
import TitleGenerator from "./components/TitleGenerator";
import ThemeToggle from "./components/ThemeToggle";
import UpgradeModal from "./components/UpgradeModal";
import ContactModal from "./components/ContactModal";
import AdminPanel from "./components/AdminPanel";
import PricingPage from "./components/PricingPage";
import PaymentResult from "./components/PaymentResult";
import AccountPage from "./components/AccountPage";
import ExamplesPage from "./components/ExamplesPage";
import NotificationsBar from "./components/NotificationsBar";
import BrandLogo, { BrandIcon } from "./components/BrandLogo";
import { DesktopNav, HeaderCTA, MobileMenu, MobileStickyCTA } from "./components/SiteNav";
import ReportSkeleton from "./components/ReportSkeleton";
import { ShareBanner, ShareFooterCTA } from "./components/results/ShareExtras";
import ResultsTopBar from "./components/results/ResultsTopBar";
import ScoreOverview from "./components/results/ScoreOverview";
import RetentionSection from "./components/results/RetentionSection";
import { KeyInsights, Recommendations } from "./components/results/InsightsSections";
import { SectionHeading } from "./components/results/ResultsPrimitives";
import ReportFeedback from "./components/ReportFeedback";
import { BrandingProvider, useBranding } from "./context/BrandingContext";
import GoogleButton from "./components/GoogleButton";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Loader2, Sparkles, TrendingUp, Target, MessageSquare, Film, Eye, Zap, Heart, AlertCircle, CheckCircle2, Lock, LogOut, Crown } from "lucide-react";
import { MIN_SCRIPT_LENGTH } from "./constants";
import { PRIORITY_COLORS } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const { user, login, logout, refreshUser } = useAuth();
  const { branding } = useBranding();
  const [scriptText, setScriptText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStartedAt, setLoadingStartedAt] = useState(null);
  const [shareMode, setShareMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState("analyze");
  const [showUpgrade, setShowUpgrade] = useState(null);
  const inputSectionRef = useRef(null);

  const handleExampleClick = (url) => {
    setMode("analyze");
    setScriptText(url);
    inputSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleViewFullFromCompare = (result) => {
    setAnalysis(result);
    setMode("analyze");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadAnalysis = async (id) => {
    setLoading(true);
    setLoadingStartedAt(Date.now());
    setError(null);
    try {
      const response = await axios.get(`${API}/analysis/${id}`);
      setAnalysis(response.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("No se pudo cargar el análisis. Puede que el enlace no sea válido.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const match = window.location.pathname.match(/^\/share\/([\w-]+)/);
    if (match) {
      setShareMode(true);
      loadAnalysis(match[1]);
    }
     
  }, []);

  const resetToHome = () => {
    setAnalysis(null);
    setScriptText("");
    setError(null);
    setCopied(false);
    if (shareMode) {
      setShareMode(false);
      window.history.pushState({}, "", "/");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/api/share/${analysis.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      window.prompt("Copia este enlace:", shareUrl);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleDownloadPdf = () => {
    window.open(`${API}/analysis/${analysis.id}/pdf`, "_blank");
  };

  if (window.location.pathname.startsWith("/admin")) {
    return <AdminPanel />;
  }

  const analyzeScript = async () => {
    if (!user) {
      login();
      return;
    }
    if (user.plan === "free" && user.remaining <= 0) {
      setShowUpgrade("quota");
      return;
    }
    if (!scriptText.trim() || scriptText.trim().length < MIN_SCRIPT_LENGTH) {
      setError(`Por favor, ingresa un guion o URL de al menos ${MIN_SCRIPT_LENGTH} caracteres`);
      return;
    }

    setLoading(true);
    setLoadingStartedAt(Date.now());
    setError(null);
    setAnalysis(null);

    try {
      const response = await axios.post(`${API}/analyze-script`, {
        script_text: scriptText
      });
      setAnalysis(response.data);
      refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "quota_exhausted") {
        setShowUpgrade("quota");
      } else if (err.response?.status === 401) {
        login();
      } else {
        setError(typeof detail === "string" ? detail : "Error al analizar el contenido. Por favor, intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <a href="/" data-testid="header-brand-icon" className="shrink-0">
                <BrandIcon size="md" />
              </a>
              <div className="min-w-0 truncate hidden sm:block">
                <BrandLogo size="md" textOnly />
                <p className="text-sm text-gray-600 hidden sm:block truncate">{branding.tagline}</p>
              </div>
            </div>

            <DesktopNav />

            {/* Auth area */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <ThemeToggle />
              <a href="/precios" data-testid="header-pricing-link" className="hidden sm:block lg:hidden text-sm font-semibold text-gray-600 hover:text-purple-700 transition-colors">
                Precios
              </a>
              <HeaderCTA />
              {user ? (
                <>
                  {user.plan === "free" ? (
                    <span data-testid="free-counter" className={`text-xs sm:text-sm font-bold px-3 py-1.5 rounded-full ${user.remaining > 0 ? "bg-purple-100 text-purple-700" : "bg-red-100 text-red-700"}`}>
                      Análisis gratuitos restantes: {user.remaining}/{user.free_limit}
                    </span>
                  ) : (
                    <span data-testid="pro-badge" className="inline-flex items-center gap-1 text-xs sm:text-sm font-bold px-2.5 sm:px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_0_14px_rgba(139,92,246,0.4)]">
                      <Crown className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Plan </span>{user.plan === "pro" ? "Pro" : "Enterprise"}
                    </span>
                  )}
                  {user.is_admin && (
                    <a href="/admin" data-testid="header-admin-link" className="text-sm font-semibold text-purple-700 hover:text-purple-900 transition-colors">
                      Admin
                    </a>
                  )}
                  <div className="flex items-center gap-2">
                    <a href="/cuenta" data-testid="profile-link" title="Mi cuenta">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-purple-200 hover:border-purple-500 transition-all cursor-pointer" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-sm font-bold cursor-pointer">
                          {(user.name || user.email)[0].toUpperCase()}
                        </span>
                      )}
                    </a>
                    <button
                      data-testid="logout-button"
                      onClick={logout}
                      title="Cerrar sesión"
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-all"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <GoogleButton compact label="Continuar con Google" />
              )}
              <MobileMenu user={user} />
            </div>
          </div>
        </div>
      </header>
      <MobileStickyCTA visible={!analysis && !shareMode && !loading} />

      <NotificationsBar />

      {/* Hero Section (landing) */}
      {!analysis && !shareMode && <HeroSection />}

      {/* Main Content */}
      <main id="analizar" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 md:pb-8 scroll-mt-24">
        {/* Input Section */}
        {!shareMode && (
        <div ref={inputSectionRef} data-testid="input-section" className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 mb-8 border-2 border-purple-200 ring-4 ring-purple-100/50 -mt-4 relative z-10">
          {/* Mode Tabs */}
          {!analysis && (
            <div data-testid="mode-tabs" className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
              <button
                data-testid="tab-analyze"
                onClick={() => setMode("analyze")}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "analyze" ? "bg-white text-purple-700 shadow" : "text-gray-500 hover:text-gray-700"}`}
              >
                Analizar Video
              </button>
              <button
                data-testid="tab-compare"
                onClick={() => {
                  if (!user) {
                    login();
                  } else if (user.plan === "free") {
                    setShowUpgrade("compare");
                  } else {
                    setMode("compare");
                  }
                }}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === "compare" ? "bg-white text-purple-700 shadow" : "text-gray-500 hover:text-gray-700"}`}
              >
                {(!user || user.plan === "free") && <Lock className="w-3.5 h-3.5 text-yellow-500" />}
                Comparar Videos
                {(!user || user.plan === "free") && (
                  <span className="text-[10px] font-bold bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-1.5 py-0.5 rounded-full">PRO</span>
                )}
              </button>
            </div>
          )}

          {mode === "compare" && !analysis ? (
            <ComparePanel onViewFull={handleViewFullFromCompare} />
          ) : (
          <>
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-800">Tu Guion o URL de YouTube</h2>
          </div>
          
          <textarea
            data-testid="script-input"
            value={scriptText}
            onChange={(e) => setScriptText(e.target.value)}
            placeholder="Pega aquí:&#10;• URL de YouTube (ej: https://www.youtube.com/watch?v=...)&#10;• Guion completo de tu video&#10;&#10;El sistema extraerá automáticamente la transcripción y metadatos si es una URL."
            className="w-full h-64 p-4 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none resize-none text-gray-700 placeholder-gray-400 transition-all"
            disabled={loading}
          />
          
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">
                {scriptText.length} caracteres {scriptText.length < MIN_SCRIPT_LENGTH && scriptText.length > 0 && (
                  <span className="text-red-500">(mínimo {MIN_SCRIPT_LENGTH})</span>
                )}
              </p>
              {user && user.plan === "free" && (
                <p data-testid="quota-hint" className={`text-xs mt-1 font-semibold ${user.remaining > 0 ? "text-purple-600" : "text-red-600"}`}>
                  {user.remaining > 0 ? `Te quedan ${user.remaining} de ${user.free_limit} análisis gratuitos` : "Has agotado tus análisis gratuitos"}
                </p>
              )}
            </div>
            
            <button
              data-testid="analyze-button"
              onClick={analyzeScript}
              disabled={loading || (user && user.remaining !== 0 && scriptText.trim().length < MIN_SCRIPT_LENGTH)}
              className="vt-cta-glow bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analizando...
                </>
              ) : !user ? (
                <>
                  <Sparkles className="w-5 h-5" />
                  Inicia sesión para analizar
                </>
              ) : user.plan === "free" && user.remaining <= 0 ? (
                <>
                  <Lock className="w-5 h-5" />
                  Analizar Contenido
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Analizar Contenido
                </>
              )}
            </button>
          </div>

          {error && (
            <div data-testid="error-message" className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          </>
          )}
        </div>
        )}

        {/* Esqueleto del informe mientras se analiza */}
        {!shareMode && loading && !analysis && (
          <div className="mt-8"><ReportSkeleton startedAt={loadingStartedAt} /></div>
        )}

        {/* Share mode loading / error */}
        {shareMode && loading && (
          <div data-testid="share-loading" className="py-8">
            <ReportSkeleton title="Cargando informe compartido..." subtitle="Un momento, estamos preparando el informe." />
          </div>
        )}
        {shareMode && error && !loading && (
          <div data-testid="share-error" className="max-w-xl mx-auto text-center py-16">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              data-testid="share-error-home-button"
              onClick={resetToHome}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
            >
              Analizar mi propio video
            </button>
          </div>
        )}

        {/* Recent Analyses (History) */}
        {!analysis && !shareMode && user && <HistorySection onSelect={loadAnalysis} />}

        {/* Results Section */}
        {analysis && (
          <div data-testid="analysis-results" className="space-y-8 animate-fadeIn">

            {shareMode && <ShareBanner analysis={analysis} />}

            {/* Nivel 1: barra superior + score principal */}
            <ResultsTopBar
              analysis={analysis} shareMode={shareMode} user={user} copied={copied}
              onReset={resetToHome} onDownloadPdf={handleDownloadPdf} onShare={handleShare}
            />
            <ScoreOverview analysis={analysis} />

            {/* Nivel 3: retención, insights y recomendaciones */}
            {analysis.retention_by_blocks && analysis.retention_by_blocks.length > 0 && (
              <RetentionSection blocks={analysis.retention_by_blocks} />
            )}
            {analysis.executive_summary && <KeyInsights summary={analysis.executive_summary} />}
            {analysis.recommendations && analysis.recommendations.length > 0 && (
              <Recommendations items={analysis.recommendations} />
            )}

            {/* Nivel 4: análisis detallado */}
            <SectionHeading eyebrow="Nivel 4" title="Análisis detallado" icon={Film} accent="violet" />

            {/* Tone & Style Analysis */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Tone */}
              {analysis.tone_analysis && (
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-purple-600" />
                    <h3 className="text-xl font-semibold text-gray-800">Análisis de Tono</h3>
                  </div>
                  
                  {analysis.tone_analysis.primary && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800">Tono Principal</span>
                        <span className="text-lg font-bold text-purple-600">{analysis.tone_analysis.primary.confidence}%</span>
                      </div>
                      <div className="text-lg font-medium text-gray-700">{analysis.tone_analysis.primary.name}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{width: `${analysis.tone_analysis.primary.confidence}%`}}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {analysis.tone_analysis.secondary && analysis.tone_analysis.secondary.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Tonos Secundarios</p>
                      <div className="space-y-2">
                        {analysis.tone_analysis.secondary.map((tone) => (
                          <div key={`tone-secondary-${tone.name}-${tone.confidence}`} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{tone.name}</span>
                            <span className="font-semibold text-purple-600">{tone.confidence}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Style */}
              {analysis.style_analysis && (
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Film className="w-5 h-5 text-purple-600" />
                    <h3 className="text-xl font-semibold text-gray-800">Análisis de Estilo</h3>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-gray-800">{analysis.style_analysis.type}</span>
                    <span className="text-lg font-bold text-purple-600">{analysis.style_analysis.confidence}%</span>
                  </div>
                  
                  {analysis.style_analysis.elements && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600 mb-2">Elementos Identificados</p>
                      <ul className="space-y-1">
                        {analysis.style_analysis.elements.map((element) => (
                          <li key={`element-${element}`} className="text-sm text-gray-700 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {element}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Audience Analysis */}
            {analysis.audience_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-purple-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Público Objetivo</h3>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Edad</p>
                    <p className="text-lg font-bold text-purple-600">{analysis.audience_analysis.age_range}</p>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Género</p>
                    <p className="text-lg font-bold text-pink-600">{analysis.audience_analysis.gender}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Nivel</p>
                    <p className="text-lg font-bold text-blue-600">{analysis.audience_analysis.knowledge_level}</p>
                  </div>
                </div>
                
                {analysis.audience_analysis.interests && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Intereses</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.audience_analysis.interests.map((interest) => (
                        <span key={`interest-${interest}`} className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {analysis.audience_analysis.psychographic && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">Perfil Psicográfico</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{analysis.audience_analysis.psychographic}</p>
                  </div>
                )}
              </div>
            )}

            {/* Keywords */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="text-xl font-semibold text-gray-800">Palabras Clave</h3>
              </div>
              <div data-testid="keywords-section" className="flex flex-wrap gap-2">
                {analysis.keywords && analysis.keywords.map((keyword) => (
                  <span
                    key={`keyword-${keyword}`}
                    className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium border border-purple-200"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Hooks Analysis */}
            {analysis.hooks && analysis.hooks.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-xl font-semibold text-gray-800">Hooks Identificados</h3>
                </div>
                
                <div className="space-y-4">
                  {analysis.hooks.map((hook) => (
                    <div key={`hook-${hook.type}-${hook.timestamp}`} className="border-l-4 border-yellow-500 pl-4 bg-yellow-50 p-4 rounded-r-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase font-bold text-yellow-700">{hook.type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">{hook.timestamp}</span>
                          <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {hook.score}/10
                          </span>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-800 mb-2">&ldquo;{hook.text}&rdquo;</p>
                      <p className="text-sm text-gray-700 mb-2"><strong>Por qué funciona:</strong> {hook.reason}</p>
                      <p className="text-sm text-gray-700"><strong>Mejora:</strong> {hook.improvement}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTAs */}
            {analysis.ctas && analysis.ctas.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Llamados a la Acción</h3>
                </div>
                
                <div className="space-y-4">
                  {analysis.ctas.map((cta) => (
                    <div key={`cta-${cta.type}-${cta.timestamp}`} className="border-l-4 border-green-500 pl-4 bg-green-50 p-4 rounded-r-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase font-bold text-green-700">{cta.type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">{cta.timestamp}</span>
                          <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {cta.effectiveness}%
                          </span>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-800 mb-2">&ldquo;{cta.text}&rdquo;</p>
                      <p className="text-sm text-gray-700 mb-1"><strong>Placement:</strong> {cta.placement}</p>
                      <p className="text-sm text-gray-700"><strong>Mejora:</strong> {cta.improvement}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emotions */}
            {analysis.emotions && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-red-500" />
                  <h3 className="text-xl font-semibold text-gray-800">Análisis Emocional</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(analysis.emotions).map(([emotion, value]) => (
                    <div key={emotion} className="text-center">
                      <div className="relative w-20 h-20 mx-auto mb-2">
                        <svg className="w-20 h-20 transform -rotate-90">
                          <circle
                            cx="40"
                            cy="40"
                            r="36"
                            stroke="#e5e7eb"
                            strokeWidth="8"
                            fill="none"
                          />
                          <circle
                            cx="40"
                            cy="40"
                            r="36"
                            stroke="#8b5cf6"
                            strokeWidth="8"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 36}`}
                            strokeDashoffset={`${2 * Math.PI * 36 * (1 - value / 100)}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold text-purple-600">{value}</span>
                        </div>
                      </div>
                      <p className="text-sm font-medium text-gray-700 capitalize">{emotion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SEO Analysis */}
            {analysis.seo_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-purple-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Análisis SEO</h3>
                </div>
                
                {analysis.seo_analysis.optimized_titles && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Títulos Optimizados</p>
                    <div className="space-y-2">
                      {analysis.seo_analysis.optimized_titles.map((title, titleIndex) => (
                        <div key={`title-${titleIndex}`} className="bg-purple-50 p-3 rounded-lg text-sm text-gray-700">
                          {titleIndex + 1}. {title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {analysis.seo_analysis.thumbnail_suggestions && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">Sugerencias de Miniatura</p>
                    <ul className="space-y-1">
                      {analysis.seo_analysis.thumbnail_suggestions.map((suggestion) => (
                        <li key={`suggestion-${suggestion.substring(0, 20)}`} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-purple-600 font-bold">•</span>
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* MÓDULOS PREMIUM NUEVOS */}

            {/* Narrative Loops */}
            {analysis.narrative_loops && analysis.narrative_loops.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Loops Narrativos Detectados</h3>
                </div>
                <div className="space-y-4">
                  {analysis.narrative_loops.map((loop) => (
                    <div key={`loop-${loop.type}-${loop.timestamp}`} className="border-l-4 border-orange-500 pl-4 bg-orange-50 p-4 rounded-r-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs uppercase font-bold text-orange-700">{loop.type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">{loop.timestamp}</span>
                          <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            {loop.quality_score}/10
                          </span>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-800 mb-2">&ldquo;{loop.text}&rdquo;</p>
                      <p className="text-sm text-gray-700 mb-1"><strong>Impacto:</strong> {loop.retention_impact}</p>
                      <p className="text-sm text-gray-700 mb-1"><strong>Por qué funciona:</strong> {loop.explanation}</p>
                      <p className="text-sm text-gray-700"><strong>Mejora:</strong> {loop.improvement}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Psychological Triggers */}
            {analysis.psychological_triggers && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Gatillos Psicológicos</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(analysis.psychological_triggers).map(([trigger, value]) => {
                    const getGradientColor = (val) => {
                      if (val >= 80) return 'from-green-400 to-green-600';
                      if (val >= 60) return 'from-yellow-400 to-yellow-600';
                      if (val >= 40) return 'from-orange-400 to-orange-600';
                      return 'from-red-400 to-red-600';
                    };
                    
                    const color = getGradientColor(value);
                    
                    return (
                      <div key={trigger} className={`bg-gradient-to-br ${color} p-4 rounded-xl text-white text-center`}>
                        <div className="text-2xl font-bold mb-1">{value}</div>
                        <div className="text-xs capitalize opacity-90">
                          {trigger.replace(/_/g, ' ')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Views Prediction */}
            {analysis.views_prediction && (
              <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-6 h-6" />
                  <h3 className="text-2xl font-bold">Predicción de Vistas</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-sm opacity-90 mb-1">Conservador</div>
                    <div className="text-3xl font-bold">{(analysis.views_prediction.conservative || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-white/30 rounded-xl p-4 backdrop-blur-sm border-2 border-white">
                    <div className="text-sm opacity-90 mb-1">Esperado</div>
                    <div className="text-3xl font-bold">{(analysis.views_prediction.expected || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                    <div className="text-sm opacity-90 mb-1">Optimista</div>
                    <div className="text-3xl font-bold">{(analysis.views_prediction.optimistic || 0).toLocaleString()}</div>
                  </div>
                </div>
                <div className="text-sm opacity-90">
                  <p>Confianza: {analysis.views_prediction.confidence_level}%</p>
                  <p>Primeras 48h: {(analysis.views_prediction.first_48h_prediction || 0).toLocaleString()} vistas</p>
                </div>
              </div>
            )}

            {/* Growth Opportunities */}
            {analysis.growth_opportunities && analysis.growth_opportunities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-purple-100">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <h3 className="text-xl font-semibold text-gray-800">Oportunidades de Crecimiento</h3>
                  <span className="text-sm text-gray-600">(Ordenadas por Impacto)</span>
                </div>
                <div className="space-y-3">
                  {analysis.growth_opportunities.map((opp, oppIndex) => {
                    const priorityColor = PRIORITY_COLORS[opp.priority] || PRIORITY_COLORS.media;
                    
                    return (
                      <div key={`opp-${opp.opportunity}`} className="border-2 border-green-200 rounded-xl p-4 bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-green-800">#{oppIndex + 1} {opp.opportunity}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${priorityColor}`}>
                            {opp.priority}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                          {opp.impact_on_ctr && (
                            <div className="bg-white/50 p-2 rounded">
                              <span className="text-gray-600">CTR:</span>
                              <span className="font-bold text-green-700 ml-1">{opp.impact_on_ctr}</span>
                            </div>
                          )}
                          {opp.impact_on_retention && (
                            <div className="bg-white/50 p-2 rounded">
                              <span className="text-gray-600">Retención:</span>
                              <span className="font-bold text-green-700 ml-1">{opp.impact_on_retention}</span>
                            </div>
                          )}
                          {opp.impact_on_views && (
                            <div className="bg-white/50 p-2 rounded">
                              <span className="text-gray-600">Views:</span>
                              <span className="font-bold text-green-700 ml-1">{opp.impact_on_views}</span>
                            </div>
                          )}
                          {opp.estimated_total_impact && (
                            <div className="bg-green-500 text-white p-2 rounded">
                              <span>Impacto Total:</span>
                              <span className="font-bold ml-1">{opp.estimated_total_impact}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mb-1">
                          <strong>Esfuerzo:</strong> {opp.effort}
                        </p>
                        {opp.implementation && (
                          <p className="text-sm text-gray-700">
                            <strong>Cómo:</strong> {opp.implementation}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Title & SEO Generator */}
            {analysis.id && (
              <TitleGenerator key={`titles-${analysis.id}`} analysis={analysis} canGenerate={!!user} onLogin={login} />
            )}

            {/* Comments Analysis (YouTube URLs only) */}
            {analysis.input_type === "url" && analysis.id && (
              <CommentsAnalysis key={analysis.id} analysis={analysis} canAnalyze={!!user} onLogin={login} />
            )}

            {/* Valoración del informe */}
            {!shareMode && user && <ReportFeedback key={`fb-${analysis.id}`} analysisId={analysis.id} />}
            {shareMode && <ShareFooterCTA />}
          </div>
        )}

      </main>

      {/* Landing Sections (visible when no analysis) */}
      {!analysis && !shareMode && (
        <>
          <FeaturesGrid />
          <HowItWorks />
          <ExampleVideos onExampleClick={handleExampleClick} />
          <SocialProof />
          <FeedbackSection />
        </>
      )}

      {/* Upgrade Modal */}
      {showUpgrade && <UpgradeModal reason={showUpgrade} onClose={() => setShowUpgrade(null)} />}
      <ContactModal />

      {/* Footer */}
      <div className="vt-brand-line" aria-hidden="true" />
      <footer className="bg-gray-900 text-gray-400 py-8 text-center text-sm">
        <p>
          <span className="font-semibold text-white">{branding.app_name}</span> — {branding.tagline}
        </p>
        <p className="mt-2 text-xs" data-testid="footer-contact">
          Soporte: <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} data-testid="footer-support-email" className="hover:text-white">{process.env.REACT_APP_SUPPORT_EMAIL}</a>
          {" · "}
          Contacto: <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`} data-testid="footer-contact-email" className="hover:text-white">{process.env.REACT_APP_CONTACT_EMAIL}</a>
        </p>
      </footer>
    </div>
  );
}

function AppRouter() {
  if (window.location.pathname.startsWith("/precios")) {
    return <PricingPage />;
  }
  if (window.location.pathname.startsWith("/payment/success")) {
    return <PaymentResult />;
  }
  if (window.location.pathname.startsWith("/payment/cancel")) {
    return <PaymentResult cancelled />;
  }
  if (window.location.pathname.startsWith("/cuenta")) {
    return <AccountPage />;
  }
  if (window.location.pathname.startsWith("/ejemplos")) {
    return <ExamplesPage />;
  }
  return <App />;
}

export default function Root() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // clientId de respaldo evita que la app se rompa si falta la variable en el build desplegado
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "missing-client-id.apps.googleusercontent.com";
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={googleClientId}>
        <BrandingProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrandingProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}
